import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, FileText, Pencil, Send, ShoppingCart, Trash2, X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useApprovePurchaseRequisition,
  useConvertPurchaseRequisition,
  useDeletePurchaseRequisition,
  usePurchaseRequisition,
  useRejectPurchaseRequisition,
  useSubmitPurchaseRequisition,
  useVendors,
} from '@/hooks/use-procurement';
import { useRequisitionPermissions } from '@/hooks/use-requisition-permissions';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { useAuth } from '@/contexts/auth-context';
import { formatMoney } from '@/lib/currency';
import { formErrorText } from '@/lib/form-errors';
import { PURCHASING_MODULE_KEY } from '@/lib/purchasing';
import { formatDateOnly } from '@/lib/date-utils';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import {
  RequisitionPriorityBadge,
  RequisitionStatusBadge,
  estimatedTotal,
} from './requisition-shared';
import { AddVendorDialog } from '@/components/shared/add-vendor-dialog';
import type { Vendor } from '@/lib/types';

/** Timestamps only (created_at / submitted_at / reviewed_at). Date-only
 * fields such as needed_by go through formatDateOnly. */
const ACTION_FIELD_LABELS: Record<string, string> = {
  vendor: 'Vendor',
  po_number: 'PO number',
  comment: 'Comment',
};

function fmtDate(value: string | null | undefined, timeZone: string, withTime = false) {
  if (!value) return '—';
  return formatDateTimeInTimeZone(
    value,
    timeZone,
    withTime ? undefined : { dateStyle: 'medium' }
  );
}

export function RequisitionDetailPage() {
  const timeZone = useOrgTimeZone();
  const orgCurrency = useOrgCurrency();
  const { id } = useParams<{ id: string }>();
  const reqId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canReview: isReviewer, canConvert: mayConvert } = useRequisitionPermissions();
  const { isAdmin } = usePermissions();
  const { isModuleEnabled } = usePlatformModules();

  const { data: req, isLoading, isError } = usePurchaseRequisition(reqId);

  const submitMut = useSubmitPurchaseRequisition();
  const approveMut = useApprovePurchaseRequisition();
  const rejectMut = useRejectPurchaseRequisition();
  const convertMut = useConvertPurchaseRequisition();
  const deleteMut = useDeletePurchaseRequisition();

  const purchasingEnabled = isModuleEnabled(PURCHASING_MODULE_KEY);
  const canConvert = mayConvert && purchasingEnabled;

  const { data: vendorsData } = useVendors(
    { page_size: 200, status: 'active' },
    canConvert && req?.status === 'approved'
  );

  const [actionError, setActionError] = useState<string | null>(null);
  const actionErrorRef = useRef<HTMLDivElement>(null);

  // Some actions (convert, approve, reject) sit well below the banner.
  useEffect(() => {
    const banner = actionErrorRef.current;
    if (actionError && banner && typeof banner.scrollIntoView === 'function') {
      banner.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [actionError]);
  const [decisionComment, setDecisionComment] = useState('');
  const [selfApproveConfirmed, setSelfApproveConfirmed] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [createdVendor, setCreatedVendor] = useState<Vendor | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (isError || !req) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Failed to load requisition.</p>
        <Button asChild className="mt-4" variant="outline">
          <Link to="/requisitions">Back to list</Link>
        </Button>
      </div>
    );
  }

  const isOwner = user?.id != null && req.requested_by === user.id;
  const canEditDraft = req.status === 'draft' && (isOwner || isReviewer);
  const canSubmit = canEditDraft && req.lines.length > 0;
  const canReview = req.status === 'submitted' && isReviewer;
  // Self-approval: only org Admins may approve their own requisition, and
  // only after explicit confirmation (logged server-side as self-approved).
  const isSelfApproval = req.status === 'submitted' && isOwner;
  const canApprove = canReview && (isAdmin || !isOwner);
  const showConvertAction = req.status === 'approved' && canConvert;

  const runAction = async (fn: () => Promise<unknown>, fallback: string) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(formErrorText(err, fallback, ACTION_FIELD_LABELS));
    }
  };

  const vendors = createdVendor
    ? [createdVendor, ...(vendorsData?.results ?? []).filter((vendor) => vendor.id !== createdVendor.id)]
    : vendorsData?.results ?? [];
  const selectedVendor = vendors.find((vendor) => vendor.id === Number(vendorId));
  const selectedVendorCurrency = selectedVendor?.default_currency || orgCurrency;
  const busy =
    submitMut.isPending ||
    approveMut.isPending ||
    rejectMut.isPending ||
    convertMut.isPending ||
    deleteMut.isPending;

  return (
    <div
      id="requisition-detail-validation"
      className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/requisitions">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 font-mono">PR-{req.id}</h1>
              <RequisitionStatusBadge status={req.status} />
              <RequisitionPriorityBadge priority={req.priority} />
            </div>
            <p className="text-gray-600 text-sm mt-1">
              Requested by{' '}
              <span className="font-medium">
                {req.requested_by_name || req.requested_by_username || 'unknown'}
              </span>{' '}
              · {fmtDate(req.created_at, timeZone)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEditDraft && (
            <>
              <Button variant="outline" asChild>
                <Link to={`/requisitions/${req.id}/edit`}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Link>
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  void runAction(async () => {
                    if (!window.confirm('Delete this draft requisition?')) return;
                    await deleteMut.mutateAsync(req.id);
                    navigate('/requisitions');
                  }, 'Failed to delete requisition')
                }
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
              <Button
                disabled={busy}
                data-validation-scope="requisition-detail-validation"
                title={canSubmit ? undefined : 'Add at least one line item first'}
                onClick={() =>
                  void runAction(() => submitMut.mutateAsync(req.id), 'Failed to submit requisition')
                }
              >
                <Send className="h-4 w-4 mr-1" />
                Submit for Approval
              </Button>
            </>
          )}
          {req.status === 'converted' && req.purchase_order_id && (
            <Button asChild>
              <Link to={`/purchase-orders/${req.purchase_order_id}`}>
                <FileText className="h-4 w-4 mr-1" />
                View {req.purchase_order_number || 'Purchase Order'}
              </Link>
            </Button>
          )}
          {showConvertAction && !showConvert && (
            <Button onClick={() => setShowConvert(true)}>
              <ShoppingCart className="h-4 w-4 mr-1" />
              Convert to Purchase Order
            </Button>
          )}
        </div>
      </div>

      {actionError && (
        <div
          ref={actionErrorRef}
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {actionError}
        </div>
      )}

      {req.status === 'rejected' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">
            Rejected by {req.reviewed_by_username || 'reviewer'} on{' '}
            {fmtDate(req.reviewed_at, timeZone, true)}
          </p>
          {req.review_comment && <p className="mt-1">{req.review_comment}</p>}
        </div>
      )}

      {req.status === 'approved' && !purchasingEnabled && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          Approved. Converting this into a purchase order requires the purchasing module (H3Books).
        </div>
      )}

      {canReview && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-base">Review this requisition</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div id="requisition-reject-fields" data-field-container>
              <Label htmlFor="decision-comment">
                Comment {showReject ? '(required to reject)' : '(optional)'}
              </Label>
              <Textarea
                id="decision-comment"
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
                rows={2}
                placeholder={
                  showReject
                    ? 'Tell the requester why this is being rejected'
                    : 'Optional note for the requester'
                }
                required={showReject}
              />
            </div>
            {isSelfApproval && isAdmin && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="self-approval-warning"
              >
                <p>
                  You raised this requisition yourself. Approving your own
                  requisition is logged as a self-approval.
                </p>
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selfApproveConfirmed}
                    onChange={(e) => setSelfApproveConfirmed(e.target.checked)}
                  />
                  I confirm I want to approve my own requisition.
                </label>
              </div>
            )}
            {isSelfApproval && !isAdmin && (
              <p className="text-sm text-gray-600" data-testid="self-approval-blocked">
                You can reject this requisition, but an org Admin must approve it
                because you raised it yourself.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {!showReject ? (
                <>
                  {canApprove && (
                    <Button
                      disabled={busy || (isSelfApproval && !selfApproveConfirmed)}
                      onClick={() =>
                        void runAction(
                          () =>
                            approveMut.mutateAsync({
                              id: req.id,
                              comment: decisionComment,
                              confirm_self_approval: isSelfApproval,
                            }),
                          'Failed to approve requisition'
                        )
                      }
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  <Button variant="outline" disabled={busy} onClick={() => setShowReject(true)}>
                    <X className="h-4 w-4 mr-1" />
                    Reject…
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="destructive"
                    data-validate-required="true"
                    data-validation-scope="requisition-reject-fields"
                    disabled={busy}
                    onClick={() =>
                      void runAction(
                        () => rejectMut.mutateAsync({ id: req.id, comment: decisionComment.trim() }),
                        'Failed to reject requisition'
                      )
                    }
                  >
                    <X className="h-4 w-4 mr-1" />
                    Confirm Reject
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={() => setShowReject(false)}>
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {showConvertAction && showConvert && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-base">Convert to a draft purchase order</CardTitle>
          </CardHeader>
          <CardContent id="convert-requisition-fields" className="space-y-3">
            <p className="text-sm text-gray-600">
              Lines are copied onto a new <strong>draft</strong> PO which then goes through the
              normal PO approval chain. Blank estimated costs become $0.00 for you to fill in.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="convert-vendor">Vendor *</Label>
                <select
                  id="convert-vendor"
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  className="mt-1 flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                  required
                >
                  <option value="">Select a vendor…</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="mt-1 text-xs text-blue-600 hover:underline"
                  onClick={() => setVendorDialogOpen(true)}
                >
                  + New vendor
                </button>
              </div>
              <div>
                <Label htmlFor="convert-po-number">PO Number</Label>
                <Input
                  id="convert-po-number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder={`PO-R${req.id} (default)`}
                />
              </div>
            </div>
            {selectedVendor && (
              <div className="space-y-1 rounded-md bg-blue-50 p-3 text-sm text-blue-950">
                <p>
                  This purchase order will be recorded in {selectedVendorCurrency} (vendor default).
                </p>
                {selectedVendorCurrency !== orgCurrency && (
                  <p>
                    Organisation currency is {orgCurrency}. Amounts are copied as entered; no
                    conversion is applied.
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                data-validate-required="true"
                data-validation-scope="convert-requisition-fields"
                disabled={busy}
                onClick={() =>
                  void runAction(async () => {
                    const result = await convertMut.mutateAsync({
                      id: req.id,
                      vendor: Number(vendorId),
                      po_number: poNumber.trim(),
                    });
                    navigate(`/purchase-orders/${result.purchase_order.id}`);
                  }, 'Failed to convert requisition')
                }
              >
                <ShoppingCart className="h-4 w-4 mr-1" />
                Create Draft PO
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => setShowConvert(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Request</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Description</p>
              <p className="text-gray-900">{req.description || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Justification</p>
              <p className="text-gray-900 whitespace-pre-wrap">{req.justification || '—'}</p>
            </div>
            {req.review_comment && req.status !== 'rejected' && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Reviewer comment</p>
                <p className="text-gray-900 whitespace-pre-wrap">{req.review_comment}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Project</dt>
                <dd className="font-medium text-gray-900">
                  {req.project ? (
                    <Link to={`/projects/${req.project}`} className="text-blue-600 hover:underline">
                      {req.project_name || `#${req.project}`}
                    </Link>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Cost Code</dt>
                <dd className="font-medium text-gray-900">{req.cost_code_label || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Needed By</dt>
                <dd className="font-medium text-gray-900">{formatDateOnly(req.needed_by)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Submitted</dt>
                <dd className="font-medium text-gray-900">
                  {fmtDate(req.submitted_at, timeZone, true)}
                </dd>
              </div>
              {req.reviewed_at && (
                <div>
                  <dt className="text-gray-500">
                    {req.status === 'rejected' ? 'Rejected' : 'Approved'} by
                  </dt>
                  <dd className="font-medium text-gray-900">
                    {req.reviewed_by_username || '—'}
                    <span className="block text-xs text-gray-500">
                      {fmtDate(req.reviewed_at, timeZone, true)}
                    </span>
                  </dd>
                </div>
              )}
              {req.purchase_order_id && (
                <div>
                  <dt className="text-gray-500">Purchase Order</dt>
                  <dd className="font-medium">
                    <Link
                      to={`/purchase-orders/${req.purchase_order_id}`}
                      className="text-blue-600 hover:underline font-mono"
                    >
                      {req.purchase_order_number || `#${req.purchase_order_id}`}
                    </Link>
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      </div>
      <AddVendorDialog
        open={vendorDialogOpen}
        onOpenChange={setVendorDialogOpen}
        onCreated={(vendor) => {
          setCreatedVendor(vendor);
          setVendorId(String(vendor.id));
        }}
      />

      <Card
        data-required="true"
        data-required-empty={req.lines.length === 0 ? 'true' : 'false'}
        data-required-message="Add at least one line item."
        tabIndex={-1}
      >
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {req.lines.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">
              No line items yet.{' '}
              {canEditDraft && (
                <Link to={`/requisitions/${req.id}/edit`} className="text-blue-600 hover:underline">
                  Add some
                </Link>
              )}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Est. Unit Cost</TableHead>
                  <TableHead className="text-right">Est. Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {req.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell className="text-gray-500">{line.line_number}</TableCell>
                    <TableCell>
                      {line.description}
                      {line.gear_model_name && (
                        <span className="block text-xs text-gray-500">{line.gear_model_name}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{Number(line.quantity)}</TableCell>
                    <TableCell className="text-right">
                      {line.estimated_unit_cost == null
                        ? '—'
                        : formatMoney(line.estimated_unit_cost, orgCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {line.estimated_unit_cost == null
                        ? '—'
                        : formatMoney(
                            Number(line.quantity) * Number(line.estimated_unit_cost),
                            orgCurrency
                          )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} className="text-right font-semibold">
                    Estimated total
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatMoney(estimatedTotal(req.lines), orgCurrency)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
